import React, { useEffect, useMemo, useRef, useState } from "react";
import FormCreator from "../../components/Forms/FormCreator";
import MolGrid from "../../components/Grid/MolGrid";
import Buttons from "../../components/ui/button/Buttons";
import { XeroPaymentListConfig } from "./data/XeroPaymentUpdateListConfig";
import { xeroPaymentListFormData } from "./data/XeroPaymentUpdateListForm.data";
import { useGetAllPaidInvoicesForProductAndServiceMutation, useXeroPaymentUpdateTaskListForProductAndServiceMutation } from "../../app/services/XeroPayment/xeroPaymentApi";
import moment from "moment";
import { APIUrl } from "../../utils/API/fetchBaseQuery";
import ToastService from "../../services/toastService/ToastService";
import { GridColumnType } from "../../data/gridColumnType";
import CenterModel from "../../components/ui/centerModel/CenterModel";

const XeroPaymentUpdateList = () => {
  const xeroPaymentlistRef = useRef();
  const molGridRef = useRef();
  const [xeroPaymentForm, setXeroPaymentForm] = useState(xeroPaymentListFormData);
  const [dataSource, setDataSource] = useState([])
  const [paginationRequest, setPaginationRequest] = useState({
    pageNo: 1,
    pageSize: 20,
    SearchText: null,
    totalCount: 0,
    PaymentStatus: null,
  });

  const [certDetailsData, setCertDetailsData] = useState([]);
  const [showCertDetailsModal, setShowCertDetailsModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [xeroPaymentUpdateTaskList, { isSuccess, isLoading, data: xeroPaymentListResponse }] = useXeroPaymentUpdateTaskListForProductAndServiceMutation();
  const [getAllPaidInvoices, { isSuccess: paidInvoicesSuccess, isLoading: paidInvoicesLoading, data: getAllPaidInvoicesData }] = useGetAllPaidInvoicesForProductAndServiceMutation();

  useEffect(() => {
      xeroPaymentUpdateTaskList(paginationRequest);
  }, [])

  useEffect(() => {
    if (!paidInvoicesLoading && paidInvoicesSuccess && getAllPaidInvoicesData) {
      if (getAllPaidInvoicesData[0]?.invoiceNumber) {
        ToastService.success("Updated xero invoice.")
        xeroPaymentUpdateTaskList(paginationRequest);
      } else {
        ToastService.warning("No PAID invoices found in Xero.")
      }
    }
  }, [paidInvoicesLoading, paidInvoicesSuccess, getAllPaidInvoicesData]);

  useEffect(() => {
    if (!isLoading && isSuccess && xeroPaymentListResponse) {
        let xeroRecord = xeroPaymentListResponse.itemList.map((item) => {
          return {
            ...item,
            overSeas: item.overseas ? 'Yes' : 'No',
            date: item.paymentDate == null ? "-" : moment(item.paymentDate).format('DD MMM YYYY'),
            invoiceLink: `${APIUrl}/Common/getPDF?type=ProductAndServicePaymentInvoice&fileName=${item?.invoicePath}`,
            totalAmount: item.totalAmount > 0 ? `$${(item?.totalAmount ?? 0).toLocaleString("en-SG", { minimumFractionDigits: 2, maximumFractionDigits: 2, })}` : '-',
          }
        })
        setPaginationRequest(prev => ({ ...prev, totalCount: xeroPaymentListResponse.totalCount }));
        setDataSource(xeroRecord);
      }
  }, [isLoading, isSuccess, xeroPaymentListResponse]);

  const handlePageChange = ({ pageNumber, pageSize }) => {
    const updatedPaginationRequest = { ...paginationRequest, pageNo: pageNumber, pageSize: pageSize };
    setPaginationRequest(updatedPaginationRequest);
    xeroPaymentUpdateTaskList(updatedPaginationRequest);
  };

  const handleSearch = () => {
    if (!xeroPaymentlistRef.current) {
      return;
    }
    const formData = xeroPaymentlistRef?.current?.getFormData();
    const updatedPaginationRequest = {
      pageNo: 1,
      pageSize: 20,
      CompanyName: formData?.companyName || null,
      PaymentStatus: formData?.paymentStatus?.label || null,
    };
    setPaginationRequest(updatedPaginationRequest);
    xeroPaymentUpdateTaskList(updatedPaginationRequest);
  };

  const handleClear = () => {
      const updatedFormData = { ...xeroPaymentForm };
      updatedFormData.initialState = {
        companyName: null,
        paymentStatus: null,
      };
      setXeroPaymentForm(updatedFormData);
  
      const request = {
        ...paginationRequest,
        pageNo: 1,
        CompanyName: null,
        PaymentStatus: null,
        totalCount: 0,
      };
      setPaginationRequest((prev) => ({ ...prev, ...request }));
      xeroPaymentUpdateTaskList(request);
  }
  
  const handleUpdate = () => {
    getAllPaidInvoices()
  }

  const openCertDetailsModal = (event, rowData) => {
    event?.stopPropagation();

    const certNumbers = (rowData?.certificateNmbers || "").split(",").map((c) => c.trim()).filter(Boolean);
    const itemCodes = (rowData?.itemCodes || "").split(",").map((c) => c.trim()).filter(Boolean);
    const pairedDetails = certNumbers.map((certNumber, index) => ({
      certNumber,
      itemCode: itemCodes[index] || "-",
    }));

    setCertDetailsData(pairedDetails);
    setShowCertDetailsModal(true);
  };

  const closeCertDetailsModal = () => {
    setShowCertDetailsModal(false);
    setCertDetailsData([]);
  };

  const xeroPaymentGridConfig = useMemo(() => {
      return {
        ...XeroPaymentListConfig,
        columns: XeroPaymentListConfig.columns.map((column) => {
          if (column.fieldName === "certificateNmbers") {
            return {
              ...column, colType: GridColumnType.CUSTOM,
              renderCustomCol: (rowData) => (
                <span
                  className="text-success custom-text-break text-decoration-underline"
                  style={{ cursor: "pointer", fontSize: "16px" }}
                  onClick={(event) => openCertDetailsModal(event, rowData)}
                >
                  {rowData?.certificateNmbers}
                </span>
              ),
            };
          }
  
          return column;
        }),
      };
    }, [selectedRows]);
    
  return (
    <>
          <div className="xero-payment-update">
            <div className="payment-update-filter row payment-update-filter-new">
              <div className="status-input">
                <FormCreator
                  ref={xeroPaymentlistRef}
                  {...xeroPaymentForm}
                />
              </div>
              <div className="xero-right-side-btn mt-0 mb-3 ">
                <div className="d-flex gap-2">
    
                  <Buttons
                    buttonText="Search"
                    buttonTypeClassName="success-btn  mb-2"
                    onClick={handleSearch}
                  />
                  <Buttons
                    buttonText="Clear"
                    buttonTypeClassName="black-btn  mb-2"
                    onClick={handleClear}
                  />
                </div>
              </div>
            </div>
            <Buttons
              buttonText="Update"
              buttonTypeClassName="xero-updatebtn btn-right"
              onClick={handleUpdate}
              isLoading={paidInvoicesLoading}
              isDisable={paidInvoicesLoading}
            />
            <div className="payment-update-list xero-payment-grid">
              <MolGrid
                ref={molGridRef}
                configuration={xeroPaymentGridConfig}
                allowPagination={true}
                dataSource={dataSource}
                isLoading={isLoading}
                pagination={paginationRequest}
                onPageChange={handlePageChange}
              />
            </div>
          </div>

           <CenterModel
                  show={showCertDetailsModal}
                  handleClose={closeCertDetailsModal}
                  modelTitle="Certificate Details"
                  modelWidth="w-45"
                >
                  <div className="common-details-table-wrapper">
                    <table className="table common-details-table">
                      <thead>
                        <tr>
                          <th>Certificates</th>
                          <th>Item Code</th>
                        </tr>
                      </thead>
          
                      <tbody>
                        {certDetailsData.length > 0 ? (
                          certDetailsData.map((cert, index) => (
                            <tr key={`${cert.certNumber}-${index}`}>
                              <td>{cert.certNumber}</td>
                              <td>{cert.itemCode}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={2} className="common-details-table-empty">
                              No certificate details available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CenterModel>
    </>
  );
};
export default XeroPaymentUpdateList;
